/* eslint-disable boundaries/element-types */
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { CheckPicker } from 'rsuite'
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree'
import { createGlobalStyle } from 'styled-components'
import { DropdownOption } from 'types'

import {
  InputAndSubtitle,
  StyledInputContainer,
  StyledLabel,
  SubtitleRowStyled,
  SubtitleTextStyled,
  TextValue,
} from './InputStyles'

export interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $itemKey?: string
  $label?: string
  $labelColor?: string
  $labelLocation?: 'top' | 'side'
  $subtitleText?: string
  $subtitleTextColor?: string
  $backgroundColor?: string
  $borderColor?: string
  units?: string
  options: DropdownOption[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  mode?: 'read' | 'edit'
  disabled?: boolean
}

const CheckPickerStyle = createGlobalStyle<{
  $backgroundColor?: string
  $borderColor?: string
}>`

  .rs-picker-default .rs-picker-toggle.rs-btn {
    padding: 0.25rem;
    border-radius: 0.25rem;
    border-color: ${({ $borderColor }) => $borderColor || 'transparent'} !important;
    background-color: ${({ $backgroundColor, theme }) =>
      $backgroundColor || theme.forecast.cardGroupBackground} !important

  }

  .rs-checkbox-checked .rs-checkbox-control .rs-checkbox-inner:before {
    border-color: ${({ $borderColor }) => $borderColor || 'transparent'} !important;
    background-color: ${({ $backgroundColor, theme }) =>
      $backgroundColor || theme.forecast.cardGroupBackground} !important
  }

  .rs-picker-value-count {
    background-color: ${({ theme }) => theme.forecast.background4} !important;
  }


  .rs-picker-check-menu .rs-checkbox-checked .rs-checkbox-inner {
    /* Override the checkbox checked color */
    background-color: white !important;
    border-color: #ccc !important; /* Example: change to a neutral color */
  }

  .rs-picker-check-menu .rs-checkbox-checked .rs-checkbox-inner:after {
    /* Override the checkmark color */
    border-color: white !important; /* Example: change to black */

  }

  .rs-check-item.rs-check-item-focus, .rs-check-item:focus, .rs-check-item:not(.rs-checkbox-disabled):hover {
    color: white;
  }

  .rs-input-group.rs-input-group-inside .rs-input {
    color: ${({ theme }) => theme.forecast.defaultText};


  }
`

const getArray = (
  fieldValue: string | number | readonly string[] | undefined
): string[] => {
  if (Array.isArray(fieldValue)) {
    return fieldValue as string[]
  }

  return []
}

export const MultiSelect = ({
  options,
  onValueChange,
  $label,
  $itemKey,
  $labelColor,
  $labelLocation,
  $subtitleText,
  $subtitleTextColor,
  $backgroundColor,
  $borderColor,
  mode,
  placeholder,
  defaultValue,
  disabled,
}: MultiSelectProps) => {
  const multiSelectContainerRef = useRef<HTMLDivElement>(null)
  const [selectedValue, setSelectedValue] = React.useState<
    string[] | undefined
  >(getArray(defaultValue))
  const [readOnlySelectedValue, setReadOnlySelectedValue] = React.useState<
    React.ReactNode[] | undefined
  >(getArray(defaultValue))

  const removeOption = () => {
    setSelectedValue(undefined)
    setReadOnlySelectedValue(undefined)
    onValueChange([])
  }

  const renderValue = (value: string[], items: ItemDataType<string>[]) => {
    const count = items.length
    if (count > 2) {
      return `${items
        .slice(0, 2)
        .map((item) => item.label)
        .join(', ')} + ${count - 2} more`
    }
    return items.map((item) => item.label).join(', ')
  }

  const resetReadOnlyLabels = useCallback(() => {
    const readOnlyLabels: React.ReactNode[] = []
    selectedValue?.map((value) => {
      const option = options.find((option) => option.value === value)
      readOnlyLabels.push(option?.label)
    })

    setReadOnlySelectedValue(readOnlyLabels)
  }, [options, selectedValue])

  useEffect(() => {
    resetReadOnlyLabels()
  }, [options, selectedValue, resetReadOnlyLabels])

  useEffect(() => {
    if (mode === 'read') {
      resetReadOnlyLabels()
      setSelectedValue(getArray(defaultValue))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, defaultValue, options])

  const formattedReadOnlyString = useMemo(() => {
    const count = readOnlySelectedValue?.length || 0
    if (count > 2 && readOnlySelectedValue) {
      return `${readOnlySelectedValue
        .slice(0, 2)
        .join(', ')} + ${count - 2} more`
    }
    return readOnlySelectedValue?.join(', ')
  }, [readOnlySelectedValue])

  return (
    <StyledInputContainer $labelLocation={$labelLocation}>
      {$label && (
        <StyledLabel $labelColor={$labelColor} htmlFor={$label}>
          {$label}
        </StyledLabel>
      )}

      <div
        ref={multiSelectContainerRef}
        style={{ position: 'relative', width: '100%' }}>
        <InputAndSubtitle>
          {mode === 'read' ? (
            <TextValue>{formattedReadOnlyString}</TextValue>
          ) : (
            <>
              <CheckPickerStyle
                $backgroundColor={$backgroundColor}
                $borderColor={$borderColor}
              />
              <CheckPicker
                data-testid={`custom-form-field-${$label}`}
                id={$itemKey}
                aria-label={$itemKey}
                aria-labelledby={$itemKey}
                name={$itemKey}
                onChange={(e) => {
                  if (e) {
                    onValueChange(e ?? [])
                    setSelectedValue(e)
                  }
                }}
                onClean={removeOption}
                searchable={true}
                value={selectedValue}
                data={options ?? []}
                aria-required='true'
                placeholder={placeholder}
                container={multiSelectContainerRef.current ?? undefined}
                renderValue={(value, items) => renderValue(value, items)}
                disabled={disabled}
              />
            </>
          )}
          {mode !== 'read' && $subtitleText && (
            <SubtitleRowStyled>
              <SubtitleTextStyled $subtitleTextColor={$subtitleTextColor}>
                {$subtitleText}
              </SubtitleTextStyled>
            </SubtitleRowStyled>
          )}
        </InputAndSubtitle>
      </div>
    </StyledInputContainer>
  )
}

MultiSelect.displayName = 'MultiSelect'
